import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAutocodeSchedules, fetchAutocodeScheduleById, updateAutocodeSchedule, deleteAutocodeSchedule, createAutocodeSchedule } from "../../fetchers/autocodeSchedule";
import { getStopReasonCodes } from "../stopReasonCodes/hooks/fetchers";
import { Box, useTheme } from "@mui/material";
import Button from "../../components/Button";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../../assets/theme";
import AutocodeSchedulesGrid from "./components/autocodeSchedulesGrid";
import { FormattedMessage } from "react-intl";
import CircularProgress from "@mui/material/CircularProgress";
import AxionControls from "../../components/controls/AxionControls";
import Popup from "../../components/Popup";
import messages from "./components/messages";
import { pushErrorMessage } from '../../utils/notifications';
import { useMsal, useAccount } from "@azure/msal-react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import AddEditSchedule from "./components/autocodeScheduleAddEditDialog";
import { fetchMachines } from "../../fetchers/machine";
import { fetchMachineTypes } from "../../fetchers/machineType";

export default function AutocodeSchedules() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { accounts } = useMsal();
    const account = useAccount(accounts[0]);
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [openPopup, setOpenPopup] = useState(false);
    const [showBackdrop, setShowBackdrop] = useState(false);
    const [autocodeSchedule, setAutocodeSchedule] = useState({
        id: "00000000-0000-0000-0000-000000000000",
        name: "",
        companyKey: companyKey,
        stopReasonCodeId: '',
        startTime: "00:00:00",
        lengthInMinutes: 60,
        mondayActive: false,
        tuesdayActive: false,
        wednesdayActive: false,
        thursdayActive: false,
        fridayActive: false,
        saturdayActive: false,
        sundayActive: false,
        lastTriggered: null,
        created: new Date().toISOString(),
        createdBy: account ? account.username : "system",
        updated: null,
        updatedBy: null,
        useCustomLength: false,
        customLengthInMinutes: null,
        splitOngoingStopOnAutocodeStart: false,
        splitOngoingStopOnAutocodeEnd: false,
        machines: []
    });

    const queryClient = useQueryClient();

    const autocodeScheduleQuery = useQuery(["autocodeSchedule", companyKey], () => fetchAutocodeSchedules(companyKey),
        {
            staleTime: 60000,
            refetchInterval: 60000,
        }
    );

    const stopReasonCodesQuery = useQuery(["stopReasonCodes", companyKey], () => getStopReasonCodes(companyKey),
        {
            staleTime: 120000,
            refetchInterval: 120000,
        }
    );

    const machineTypesQuery = useQuery(
        ["machineTypes"],
        () => fetchMachineTypes());

    const machinesQuery = useQuery(["machines", companyKey], () => fetchMachines(companyKey),
        {
            staleTime: 60000,
            refetchInterval: 60000,
        }
    );

    const lengthOptions = [];

    // Första 2 timmarna: 15 min intervaller (30–120)
    for (let minutes = 15; minutes <= 120; minutes += 15) {
        const hours = Math.floor(minutes / 60);
        const remainder = minutes % 60;

        let label = "";
        if (hours > 0) {
            label += `${hours} h`;
        }
        if (remainder > 0) {
            label += ` ${remainder} min`;
        }

        lengthOptions.push({
            id: minutes,
            label: label.trim()
        });
    }

    // 2–6 timmar: 30 min intervaller (150–360)
    for (let minutes = 150; minutes <= 360; minutes += 30) {
        const hours = Math.floor(minutes / 60);
        const remainder = minutes % 60;

        let label = "";
        if (hours > 0) {
            label += `${hours} h`;
        }
        if (remainder > 0) {
            label += ` ${remainder} min`;
        }

        lengthOptions.push({
            id: minutes,
            label: label.trim()
        });
    }

    // 7–72 timmar: 60 min intervaller (420–4320)
    for (let minutes = 420; minutes <= 4320; minutes += 60) {
        const hours = minutes / 60;
        lengthOptions.push({
            id: minutes,
            label: `${hours} h`
        });
    }

    const handleEditClick = (id) => {
        setShowBackdrop(true);
        fetchAutocodeScheduleById(id).then((autocodeSchedule) => {
            const predefinedValues = lengthOptions.map(l => l.id);
            const isPredefined = predefinedValues.includes(autocodeSchedule.lengthInMinutes);

            if (!isPredefined) {
                autocodeSchedule.useCustomLength = true;
                autocodeSchedule.customLengthInMinutes = autocodeSchedule.lengthInMinutes;
            }

            setAutocodeSchedule(autocodeSchedule);
            setOpenPopup(true);
            setShowBackdrop(false);
        }).catch((error) => {
            console.log(error);
            setShowBackdrop(false);
            pushErrorMessage(`Cannot fetch the schedule.`);
        });
    };

    const handleDeleteClick = (id) => {
        setShowBackdrop(true);
        deleteAutocodeSchedule(id).then(() => {
            queryClient.invalidateQueries({
                queryKey: ["autocodeSchedule", companyKey]
            });
            autocodeScheduleQuery.refetch();
            setShowBackdrop(false);
        }).catch((error) => {
            console.log(error);
            setShowBackdrop(false);
            pushErrorMessage(`Cannot delete the schedule.`);
        });
    }

    const handleChange = (event) => {
        const { id, name, value, type, checked } = event.target
        setAutocodeSchedule({
            ...autocodeSchedule,
            [id ? id : name]: type === "checkbox" ? checked : value
        });
    };

    const handleAddClick = () => {
        setAutocodeSchedule({
            id: "00000000-0000-0000-0000-000000000000",
            name: "",
            companyKey: companyKey,
            stopReasonCodeId: '',
            startTime: "00:00:00",
            lengthInMinutes: 60,
            useCustomLength: false,
            customLengthInMinutes: null,
            mondayActive: false,
            tuesdayActive: false,
            wednesdayActive: false,
            thursdayActive: false,
            fridayActive: false,
            saturdayActive: false,
            sundayActive: false,
            lastTriggered: null,
            created: new Date().toISOString(),
            createdBy: account ? account.username : "system",
            updated: null,
            updatedBy: null,
            splitOngoingStopOnAutocodeStart: false,
            splitOngoingStopOnAutocodeEnd: false,
            machines: []
        });
        setOpenPopup(true);
    };

    const handleSave = () => {
        setShowBackdrop(true);
        if (autocodeSchedule.id !== "00000000-0000-0000-0000-000000000000") {
            autocodeSchedule.updatedBy = account.username;
            if (autocodeSchedule.useCustomLength) {
                autocodeSchedule.lengthInMinutes = autocodeSchedule.customLengthInMinutes;
            }

            updateAutocodeSchedule(autocodeSchedule).then(() => {
                setOpenPopup(false);
                queryClient.invalidateQueries({
                    queryKey: ["autocodeSchedule", companyKey]
                });
                autocodeScheduleQuery.refetch();
                setShowBackdrop(false);
            }).catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Cannot update the schedule.`);
            });
        }
        else {
            autocodeSchedule.updatedBy = account.username;
            autocodeSchedule.createdBy = account.username;
            if (autocodeSchedule.useCustomLength) {
                autocodeSchedule.lengthInMinutes = autocodeSchedule.customLengthInMinutes;
            }
            createAutocodeSchedule(autocodeSchedule).then(() => {
                setOpenPopup(false);
                autocodeScheduleQuery.refetch();
                setShowBackdrop(false);
            }).catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Cannot create the schedule.`);
            });
        }
    };

    if (autocodeScheduleQuery.isLoading || stopReasonCodesQuery.isLoading || machinesQuery.isLoading)
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress color="secondary" /></div>;

    return (
        <Box>
            <Button
                text={<FormattedMessage {...messages.addScheduleLabel} />}
                variant="outlined"
                onClick={handleAddClick}
                startIcon={<AddIcon />}
                sx={{
                    "&:hover": {
                        backgroundColor: colors.dashbordGreen[700],
                    },
                    backgroundColor: colors.dashbordGreen[500],
                    color: "#000000",
                    position: "absolute",
                    right: "10px",
                }} />
            <AutocodeSchedulesGrid
                autocodeSchedules={autocodeScheduleQuery}
                handleDeleteClick={handleDeleteClick}
                handleEditClick={handleEditClick}
                stopReasonCodes={stopReasonCodesQuery?.data}
            />
            <Popup
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
                maxWidht="sm"
                title={autocodeSchedule.id !== "00000000-0000-0000-0000-000000000000" ? <FormattedMessage {...messages.editScheduleLabel} /> : <FormattedMessage {...messages.addScheduleLabel} />}>
                <AddEditSchedule
                    onChange={handleChange}
                    handleSave={handleSave}
                    autocodeSchedule={autocodeSchedule}
                    stopReasonCodes={stopReasonCodesQuery?.data}
                    lengthOptions={lengthOptions}
                    setOpenPopup={setOpenPopup}
                    machines={machinesQuery?.data}
                    machineTypes={machineTypesQuery?.data} />
            </Popup>
            <AxionControls.AxionBackdrop open={showBackdrop} />
        </Box>
    );
}
